import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormGroup, UntypedFormBuilder } from '@angular/forms';
import { Client } from '@core/types/client';
import { ApisService } from '@app/core/services/apis-service';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { PepRawJsonDialogComponent } from './pep-raw-json-dialog/pep-raw-json-dialog.component';
import { PepRecord } from './pep-record.interface';

@Component({
  selector: 'itfg-risk-indicators-edit',
  templateUrl: './risk-indicators-edit.component.html',
  styleUrls: ['./risk-indicators-edit.component.scss'],
  standalone: false
})
export class RiskIndicatorsEditComponent implements OnInit, OnChanges {
  @Input() client: Client;
  @Input() isNewItem: boolean;

  riskIndicatorsForm: FormGroup;

  pepMatch: boolean | null = null;
  pepReportData: PepRecord[] = [];
  showPepReport: boolean = false;
  isLoadingPepReport: boolean = false;
  pepReportRawData: any = null;
  pepError: boolean = false;
  pepReportError: boolean = false;

  private hasInitialized = false;

  // Regdata fields
  seizureMatch: boolean | null = null;
  napMatch: boolean | null = null;
  regdataError: boolean = false;

  constructor(
    private fb: UntypedFormBuilder,
    private apisService: ApisService,
    private translate: TranslateService,
    private dialog: MatDialog
  ) { }

  ngOnInit() {
    this.createForm();
    this.hasInitialized = true;
    if (this.client) {
      this.checkPepStatus();
      this.searchRegdata();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.client || !this.client) {
      return;
    }

    if (!this.hasInitialized || changes.client.firstChange) {
      return;
    }

    this.checkPepStatus();
    this.searchRegdata();
  }

  checkPepStatus() {
    if (this.client && (this.client.civilId || this.client.fullName)) {
      this.apisService
        .checkPepStatus(this.client.civilId, this.client.fullName)
        .subscribe({
          next: (result) => {
            this.pepError = false;
            this.pepMatch = result;
            const key = result ? 'riskIndicators.match' : 'riskIndicators.nomatch';
            this.translate.get(key).subscribe((res) => {
              this.riskIndicatorsForm.patchValue({ pep: res });
            });
          },
          error: (error) => {
            console.error('Error fetching PEP status:', error);
            this.pepError = true;
            this.pepMatch = null;
            this.riskIndicatorsForm.patchValue({ pep: this.getApisUnavailableText() });
          }
        });
    }
  }

  searchRegdata() {
    if (this.client && this.client.civilId && this.client.mobile) {
      this.apisService.searchRegdata(this.client.civilId, this.client.mobile).subscribe({
        next: (result) => {       
          this.regdataError = false;

          // Update company participation
          this.riskIndicatorsForm.patchValue({
            companyParticipation: result.numberOfCompaniesParticipating || 0
          });

          // Update seizure field
          this.seizureMatch = result.hasGeneralSeizureEntry;
          const seizureKey = result.hasGeneralSeizureEntry ? 'riskIndicators.match' : 'riskIndicators.nomatch';
          this.translate.get(seizureKey).subscribe((res) => {
            this.riskIndicatorsForm.patchValue({ seizure: res });
          });

          // Update NAP field
          this.napMatch = result.hasNapEntry;
          const napKey = result.hasNapEntry ? 'riskIndicators.match' : 'riskIndicators.nomatch';
          this.translate.get(napKey).subscribe((res) => {
            this.riskIndicatorsForm.patchValue({ nap: res });
          });

          // Update phone match field
          this.riskIndicatorsForm.patchValue({
            phoneMatch: result.phoneMatch || ''
          });
        },
        error: (error) => {
          console.error('Error fetching regdata:', error);
          this.regdataError = true;
          this.seizureMatch = null;
          this.napMatch = null;
          const errorText = this.getApisUnavailableText();
          this.riskIndicatorsForm.patchValue({
            companyParticipation: errorText,
            seizure: errorText,
            nap: errorText,
            phoneMatch: errorText
          });
        }
      });
    }
  }

  onInfoClick() {
    // Toggle visibility
    this.showPepReport = !this.showPepReport;

    // Fetch data if opening (removed cache check for debugging)
    if (this.showPepReport && this.client && this.client.civilId) {
      this.isLoadingPepReport = true;
      this.pepReportError = false;
      this.apisService.getPepReport(this.client.civilId).subscribe({
        next: (report) => {
          // Store raw data for JSON display
          this.pepReportRawData = report;
          // Extract records array from report.data.records
          const allRecords = report?.data?.records || [];
          // Filter to only show records that are active or within last 12 months
          const relevantRecords = allRecords.filter((record: PepRecord) => this.isRecordRelevant(record.toDate));
          // Remove duplicates by position+institution, keep the most recent value by recordDate
          this.pepReportData = this.filterByLatestRecordDate(relevantRecords);
          this.isLoadingPepReport = false;
          this.pepReportError = false;
        },
        error: (error) => {
          console.error('Error fetching PEP report:', error);
          this.isLoadingPepReport = false;
          this.pepReportError = true;
          this.pepReportRawData = null;
          this.pepReportData = [];
        }
      });
    }
  }

  private isRecordRelevant(toDate: string | null): boolean {
    // Show if toDate is null or empty (active)
    if (!toDate) {
      return true;
    }

    // Show if toDate is in the future or within last 12 months
    const toDateObj = new Date(toDate);
    const today = new Date();
    const twelveMonthsAgo = new Date();
    twelveMonthsAgo.setMonth(today.getMonth() - 12);

    // Include if toDate >= 12 months ago (this includes future dates)
    return toDateObj >= twelveMonthsAgo;
  }

  private filterByLatestRecordDate(records: PepRecord[]): PepRecord[] {
    const byKey = new Map<string, PepRecord>();

    records.forEach((record) => {
      const key = `${record.xmlPosition || ''}||${record.xmlWork || ''}`;
      const existing = byKey.get(key);

      if (!existing) {
        byKey.set(key, record);
        return;
      }

      const existingDate = this.parseDateOrMin(existing.recordDate);
      const currentDate = this.parseDateOrMin(record.recordDate);
      if (currentDate >= existingDate) {
        byKey.set(key, record);
      }
    });

    return Array.from(byKey.values());
  }

  private parseDateOrMin(value?: string | null): number {
    if (!value) {
      return Number.NEGATIVE_INFINITY;
    }
    const ts = Date.parse(value);
    return Number.isNaN(ts) ? Number.NEGATIVE_INFINITY : ts;
  }

  calculatePepStatus(toDate: string | null): string {
    if (!toDate) {
      return this.translate.instant('riskIndicators.statusActive');
    }

    const toDateObj = new Date(toDate);
    const today = new Date();
    const twelveMonthsAgo = new Date();
    twelveMonthsAgo.setMonth(today.getMonth() - 12);

    if (toDateObj >= twelveMonthsAgo && toDateObj <= today) {
      return this.translate.instant('riskIndicators.statusActiveLast12Months');
    }
    else if (toDateObj > today) {
      return this.translate.instant('riskIndicators.statusActive');
    }

    return this.translate.instant('riskIndicators.statusNotActive');
  }

  onShowRawJsonClick() {
    if (!this.client || !this.client.civilId) {
      console.warn('No civil ID available for PEP report');
      return;
    }

    // Fetch fresh data if not already loaded
    if (!this.pepReportRawData) {
      this.apisService.getPepReport(this.client.civilId).subscribe({
        next: (report) => {
          this.pepReportRawData = report;
          this.pepReportError = false;
          this.openJsonDialog(report);
        },
        error: (error) => {
          console.error('Error fetching PEP report:', error);
          this.pepReportRawData = null;
          this.pepReportError = true;
          this.openJsonDialog({ error: this.getApisUnavailableText() });
        }
      });
    } else {
      this.openJsonDialog(this.pepReportRawData);
    }
  }

  private openJsonDialog(data: any) {
    this.dialog.open(PepRawJsonDialogComponent, {
      width: '800px',
      maxHeight: '80vh',
      autoFocus: false,
      data: { report: data },
    });
  }
  private getApisUnavailableText(): string {
    return this.translate.instant('riskIndicators.apisUnavailable');
  }

  createForm() {
    this.riskIndicatorsForm = this.fb.group({
      pep: [{ value: '', disabled: true }],
      companyParticipation: [{ value: '', disabled: true }],
      seizure: [{ value: '', disabled: true }],
      nap: [{ value: '', disabled: true }],
      phoneMatch: [{ value: '', disabled: true }]
    });
  }
}
